/**
 * Modal — generic floating window matching the legacy SWF popups
 * (profileWindow / adminWindow / evilAdminWindow / optionsWindow /
 * arnoldSoundBoard / randomSoundBoard). All five legacy panels were
 * MovieClips placed at fixed positions with a small grey backdrop
 * and a red × close button top-right.
 *
 * Faithful chrome: dark-grey panel `#5a5a5a` background, white drop-
 * shadow text labels, red close glyph. Centred on the chat stage with
 * a subtle scrim behind it.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { playUi } from '../lib/uiSound.js';

interface Props {
    title?: string;
    onClose: () => void;
    /** Optional CSS-class hook so callers can scope custom styles. */
    className?: string;
    /** Click-outside dismiss (default: true). */
    dismissOnBackdrop?: boolean;
    children: ReactNode;
}

export function Modal({ title, onClose, className, dismissOnBackdrop = true, children }: Props) {
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        playUi('whoosh');
        function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
        document.addEventListener('keydown', onKey);
        return () => {
            playUi('close');
            document.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    return (
        <div className="pc-modal-scrim"
             role="presentation"
             onMouseDown={dismissOnBackdrop ? (e) => {
                 if (e.target === e.currentTarget) onClose();
             } : undefined}>
            <div className={`pc-modal ${className ?? ''}`} ref={ref} role="dialog" aria-label={title}>
                {title !== undefined && (
                    <div className="pc-modal-header">
                        <span className="pc-modal-title">{title}</span>
                        <button className="pc-modal-close" onClick={onClose} title="close" aria-label="close">×</button>
                    </div>
                )}
                {title === undefined && (
                    <button className="pc-modal-close pc-modal-close-floating" onClick={onClose} title="close" aria-label="close">×</button>
                )}
                <div className="pc-modal-body">{children}</div>
            </div>
        </div>
    );
}
